/**
 * Mobile read-only review, ambulatory diary correlation, sleep staging hooks,
 * and Chatwoot support-loop linkage for PMS/CAPA/risk/feature records.
 */

export const SUPPORT_LOOP_SCHEMA_VERSION = "support-loop-v1.0.0";

export type MobileReviewMode = {
  enabled: boolean;
  readOnly: boolean;
  allowedControls: Array<
    "seek" | "play_pause" | "montage_select" | "filter_view" | "jump_event"
  >;
  blockedControls: Array<
    "annotate" | "edit_report" | "artifact_reduction" | "sign_report"
  >;
};

export function createMobileReadOnlyMode(enabled = true): MobileReviewMode {
  return {
    enabled,
    readOnly: true,
    allowedControls: ["seek", "play_pause", "montage_select", "filter_view", "jump_event"],
    blockedControls: ["annotate", "edit_report", "artifact_reduction", "sign_report"],
  };
}

export function isMobileControlAllowed(
  mode: MobileReviewMode,
  control: string,
): boolean {
  if (!mode.enabled) return true;
  if (mode.blockedControls.includes(control as MobileReviewMode["blockedControls"][number])) {
    return false;
  }
  return mode.allowedControls.includes(
    control as MobileReviewMode["allowedControls"][number],
  );
}

export type AmbulatoryDiaryEntry = {
  entryId: string;
  timeSeconds: number;
  label: string;
  note?: string | null;
};

export type AmbulatoryCorrelation = {
  entryId: string;
  nearestEventId: string | null;
  deltaSeconds: number | null;
  correlated: boolean;
};

export function correlateDiaryToEvents(
  diary: AmbulatoryDiaryEntry[],
  events: Array<{ eventId: string; timeSeconds: number }>,
  windowSeconds = 120,
): AmbulatoryCorrelation[] {
  return diary.map((entry) => {
    let best: { eventId: string; delta: number } | null = null;
    for (const event of events) {
      const delta = Math.abs(event.timeSeconds - entry.timeSeconds);
      if (delta <= windowSeconds && (!best || delta < best.delta)) {
        best = { eventId: event.eventId, delta };
      }
    }
    return {
      entryId: entry.entryId,
      nearestEventId: best?.eventId ?? null,
      deltaSeconds: best?.delta ?? null,
      correlated: Boolean(best),
    };
  });
}

export function compressLongStudyWindows(
  durationSeconds: number,
  targetWindows = 24,
): Array<{ start: number; end: number }> {
  const duration = Math.max(0, durationSeconds);
  const windows = Math.max(1, targetWindows);
  const step = duration / windows;
  const out: Array<{ start: number; end: number }> = [];
  for (let i = 0; i < windows; i += 1) {
    const start = i * step;
    const end = i === windows - 1 ? duration : (i + 1) * step;
    out.push({ start, end });
  }
  return out;
}

export type SleepStageCode = "W" | "N1" | "N2" | "N3" | "REM" | "U";

export type SleepStageEpoch = {
  startTimeSeconds: number;
  endTimeSeconds: number;
  stage: SleepStageCode;
  confidence: number;
};

export function summarizeSleepStages(epochs: SleepStageEpoch[]): Record<
  SleepStageCode,
  { seconds: number; percent: number }
> {
  const totals: Record<SleepStageCode, number> = {
    W: 0,
    N1: 0,
    N2: 0,
    N3: 0,
    REM: 0,
    U: 0,
  };
  let duration = 0;
  for (const epoch of epochs) {
    const seconds = Math.max(0, epoch.endTimeSeconds - epoch.startTimeSeconds);
    totals[epoch.stage] += seconds;
    duration += seconds;
  }
  const summary = {} as Record<SleepStageCode, { seconds: number; percent: number }>;
  (Object.keys(totals) as SleepStageCode[]).forEach((stage) => {
    summary[stage] = {
      seconds: totals[stage],
      percent: duration > 0 ? (totals[stage] / duration) * 100 : 0,
    };
  });
  return summary;
}

export type SupportIssueClass =
  | "bug"
  | "howto"
  | "billing"
  | "integration"
  | "clinical_safety"
  | "feature_request"
  | "other";

export type SupportEscalationPath =
  | "l1_support"
  | "l2_engineering"
  | "clinical_safety"
  | "capa"
  | "security";

export type SupportConversationLink = {
  conversationId: string;
  featureId?: string | null;
  riskId?: string | null;
  capaId?: string | null;
  pmsRecordId?: string | null;
  issueClass: SupportIssueClass;
  escalationPath: SupportEscalationPath;
  autoCloseEligible: boolean;
  customerImpact: "low" | "medium" | "high" | "critical";
};

export function classifySupportIssue(text: string): SupportIssueClass {
  const t = text.toLowerCase();
  if (t.includes("safety") || t.includes("patient harm") || t.includes("misdiagnosis")) {
    return "clinical_safety";
  }
  if (t.includes("hl7") || t.includes("fhir") || t.includes("emr") || t.includes("integration")) {
    return "integration";
  }
  if (t.includes("invoice") || t.includes("billing") || t.includes("subscription")) {
    return "billing";
  }
  if (t.includes("how do i") || t.includes("how to") || t.includes("guide")) {
    return "howto";
  }
  if (t.includes("feature") || t.includes("enhancement") || t.includes("request")) {
    return "feature_request";
  }
  if (t.includes("error") || t.includes("bug") || t.includes("crash") || t.includes("fail")) {
    return "bug";
  }
  return "other";
}

export function resolveEscalationPath(
  issueClass: SupportIssueClass,
): SupportEscalationPath {
  switch (issueClass) {
    case "clinical_safety":
      return "clinical_safety";
    case "bug":
    case "integration":
      return "l2_engineering";
    case "feature_request":
      return "capa";
    case "billing":
      return "l1_support";
    default:
      return "l1_support";
  }
}

export function isAutoCloseEligible(input: {
  issueClass: SupportIssueClass;
  hoursSinceLastCustomerMessage: number;
  labeledResolved: boolean;
}): boolean {
  if (input.issueClass === "clinical_safety") return false;
  if (!input.labeledResolved) return false;
  return input.hoursSinceLastCustomerMessage >= 72;
}

export function linkChatwootConversation(input: {
  conversationId: string;
  text: string;
  featureId?: string | null;
  riskId?: string | null;
  capaId?: string | null;
  pmsRecordId?: string | null;
  hoursSinceLastCustomerMessage?: number;
  labeledResolved?: boolean;
  customerImpact?: SupportConversationLink["customerImpact"];
}): SupportConversationLink {
  const issueClass = classifySupportIssue(input.text);
  return {
    conversationId: input.conversationId,
    featureId: input.featureId ?? null,
    riskId: input.riskId ?? null,
    capaId: input.capaId ?? null,
    pmsRecordId: input.pmsRecordId ?? null,
    issueClass,
    escalationPath: resolveEscalationPath(issueClass),
    autoCloseEligible: isAutoCloseEligible({
      issueClass,
      hoursSinceLastCustomerMessage: input.hoursSinceLastCustomerMessage ?? 0,
      labeledResolved: Boolean(input.labeledResolved),
    }),
    customerImpact: input.customerImpact || "medium",
  };
}

export function summarizeCustomerImpact(
  links: SupportConversationLink[],
): Record<SupportConversationLink["customerImpact"], number> {
  const counts = { low: 0, medium: 0, high: 0, critical: 0 };
  for (const link of links) counts[link.customerImpact] += 1;
  return counts;
}
